import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatApiError, api } from "@/lib/api";
import { toast } from "sonner";
import Seo from "@/components/Seo";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      // Verify the session cookie actually persisted (some in-app browsers
      // — e.g. dentro de WhatsApp — block cookies silently).
      try {
        await api.get("/auth/me", { timeout: 10000 });
      } catch (verifyErr) {
        if (verifyErr?.response?.status === 401) {
          const msg = "Tu navegador está bloqueando las cookies de sesión. " +
            "Abre la aplicación directamente en Chrome o Safari (menú ⋮ / compartir → \"Abrir en el navegador\") e inténtalo de nuevo.";
          setError(msg);
          toast.error(msg);
          return;
        }
      }
      toast.success("¡Bienvenido!");
      navigate("/dashboard");
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen emerald-gradient flex items-center justify-center p-4">
      <Seo
        title="Iniciar sesión · EF Concepción — Gestión Deportiva"
        description="Accede al panel de gestión del club EF Concepción: asistencia, convocatorias, minutos, evaluaciones, pizarra táctica y biblioteca de tareas para entrenadores."
        path="/login"
      />
      <div className="w-full max-w-5xl grid md:grid-cols-2 gap-6 items-center">
        <div className="hidden md:block">
          <div className="inline-flex items-center gap-3 mb-6">
            <img src="/logo.jpg" alt="EF Concepción" className="w-14 h-14 rounded-xl object-contain bg-white border border-slate-200 shadow-sm" />
            <div>
              <div className="font-black text-2xl leading-tight" style={{ fontFamily: "Nunito" }}>EF Concepción</div>
              <div className="text-xs uppercase tracking-widest text-slate-500">Madrid · Gestión</div>
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black leading-tight mb-4" style={{ fontFamily: "Nunito" }}>
            Gestiona tu equipo
            <br />
            <span className="text-purple-700">como un profesional.</span>
          </h1>
          <p className="text-slate-600 text-lg leading-relaxed max-w-md">
            Controla la asistencia, las convocatorias y los minutos jugados de todos tus jugadores.
            Perfecto para fútbol base y amateur.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4">
            {[
              { n: "100%", t: "Gratis" },
              { n: "∞", t: "Equipos" },
              { n: "24/7", t: "Acceso" },
            ].map((s) => (
              <div key={s.t} className="p-4 bg-white rounded-lg border border-slate-200">
                <div className="text-2xl font-black text-purple-700" style={{ fontFamily: "Nunito" }}>{s.n}</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">{s.t}</div>
              </div>
            ))}
          </div>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-2xl font-black" style={{ fontFamily: "Nunito" }}>
              Inicia sesión
            </CardTitle>
            <p className="text-sm text-slate-500">Accede a tu panel de gestión</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              <div>
                <Label htmlFor="email">Correo electrónico</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <Label htmlFor="password">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                />
                <div className="text-right mt-1">
                  <Link
                    to="/forgot-password"
                    className="text-xs text-slate-500 hover:text-purple-700"
                    data-testid="link-forgot"
                  >
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>
              </div>
              {error && (
                <p className="text-sm text-red-600" data-testid="login-error">
                  {error}
                </p>
              )}
              <Button
                type="submit"
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? "Entrando..." : "Entrar"}
              </Button>
              <div className="text-sm text-center text-slate-500">
                ¿No tienes cuenta?{" "}
                <Link to="/register" className="text-purple-700 font-semibold" data-testid="link-register">
                  Regístrate
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
